import { useEffect, useState } from 'react';

type Cadence = 'daily' | 'weekly' | 'monthly' | 'quarterly';
type Format = 'PDF' | 'HTML' | 'CSV' | 'XLSX';

type Report = {
  id: string;
  name: string;
  scope: string;
  contents: string[];
  cadence: Cadence;
  cadenceDay: string;
  cadenceTime: string;
  next: string;
  recipients: string[];
  format: Format;
  active: boolean;
};

const CADENCE_LABEL: Record<Cadence, string> = {
  daily: 'Täglich',
  weekly: 'Wöchentlich',
  monthly: 'Monatlich',
  quarterly: 'Quartalsweise',
};

const CONTENT_OPTIONS = [
  'Overview',
  'Insights',
  'Worst Performer',
  'Top Movers',
  'Land-Performance',
  'Pending-Liste',
  'Hidden-Gründe',
  'AI-Empfehlungen',
];

const SEED: readonly Report[] = [
  {
    id: 'r1',
    name: 'Wöchentlicher Overview — Geschäftsleitung',
    scope: 'Alle Shops',
    contents: ['Overview', 'Top Movers', 'AI-Empfehlungen'],
    cadence: 'weekly',
    cadenceDay: 'Montag',
    cadenceTime: '08:00',
    next: 'Mo 05.05.2026',
    recipients: ['cfo@tomi.com', 'ceo@tomi.com', 'po@tomi.com', 'qa@tomi.com'],
    format: 'PDF',
    active: true,
  },
  {
    id: 'r2',
    name: 'Daily Digest — Moderationsteam',
    scope: 'Alle Shops',
    contents: ['Pending-Liste', 'Hidden-Gründe'],
    cadence: 'daily',
    cadenceDay: '',
    cadenceTime: '07:00',
    next: 'Morgen 07:00',
    recipients: ['mod-team@tomi.com', 'sofia.conti@tamaris.com'],
    format: 'HTML',
    active: true,
  },
  {
    id: 'r3',
    name: 'Monatlicher Insights — Tamaris',
    scope: 'DE, AT, CH',
    contents: ['Insights', 'Worst Performer', 'Land-Performance'],
    cadence: 'monthly',
    cadenceDay: '1.',
    cadenceTime: '09:00',
    next: 'So 01.05.2026',
    recipients: [
      'po@tamaris.com',
      'qa@tamaris.com',
      'gl@tamaris.com',
      'marco@wortmann-retail.com',
      'elena@tomi.com',
      'lukas@tomi.com',
    ],
    format: 'PDF',
    active: true,
  },
  {
    id: 'r4',
    name: 'Worst-Performer Alert',
    scope: 'Alle Shops',
    contents: ['Worst Performer'],
    cadence: 'weekly',
    cadenceDay: 'Freitag',
    cadenceTime: '16:00',
    next: 'Fr 02.05.2026',
    recipients: ['po@tomi.com', 'qa@tomi.com', 'category-managers@tomi.com'],
    format: 'XLSX',
    active: false,
  },
];

export function AutoReportsPage() {
  const [reports, setReports] = useState<Report[]>([...SEED]);
  const [editingId, setEditingId] = useState<string | null>(null);

  function toggle(id: string) {
    setReports((rs) => rs.map((r) => (r.id === id ? { ...r, active: !r.active } : r)));
  }

  function update(id: string, patch: Partial<Report>) {
    setReports((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  const editing = editingId ? reports.find((r) => r.id === editingId) ?? null : null;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h2 className="page-title">Auto-Reports</h2>
          <p className="page-sub">
            Periodische Versendung ausgewählter Dashboards und Auswertungen an Verteiler.
          </p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn primary">
            <span className="material-symbols-rounded">add</span>
            Neuer Auto-Report
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Name</th>
              <th>Inhalt</th>
              <th>Rhythmus</th>
              <th>Nächster Versand</th>
              <th>Empfänger</th>
              <th>Format</th>
              <th>Aktiv</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id} onClick={() => setEditingId(r.id)} style={{ cursor: 'pointer' }}>
                <td>
                  <div style={{ font: '600 13px/18px var(--font-body)' }}>{r.name}</div>
                  <div style={{ font: '500 11px/15px var(--font-mono)', color: 'var(--fg-3)' }}>{r.scope}</div>
                </td>
                <td>
                  <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                    {r.contents.map((c) => (
                      <span key={c} className="chip" style={{ padding: '2px 10px', font: '500 11px/15px var(--font-body)' }}>
                        {c}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  {CADENCE_LABEL[r.cadence]}
                  {r.cadenceDay && ` · ${r.cadenceDay}`} · {r.cadenceTime}
                </td>
                <td className="mono" style={{ color: 'var(--fg-2)' }}>{r.next}</td>
                <td className="mono" style={{ color: 'var(--fg-2)' }}>{r.recipients.length}</td>
                <td>
                  <span className={`badge ${r.format === 'PDF' ? 'pending' : r.format === 'HTML' ? 'needs_review' : 'published'}`}>
                    {r.format}
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className={`switch${r.active ? ' on' : ''}`}
                    onClick={() => toggle(r.id)}
                    aria-label={r.active ? 'Deaktivieren' : 'Aktivieren'}
                  />
                </td>
                <td>
                  <span className="material-symbols-rounded" style={{ color: 'var(--fg-3)', fontSize: 18 }}>
                    chevron_right
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ReportEditor
          report={editing}
          onClose={() => setEditingId(null)}
          onChange={(patch) => update(editing.id, patch)}
        />
      )}
    </div>
  );
}

function ReportEditor({
  report,
  onClose,
  onChange,
}: {
  report: Report;
  onClose: () => void;
  onChange: (patch: Partial<Report>) => void;
}) {
  const [recipientInput, setRecipientInput] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function addRecipient(email: string) {
    const trimmed = email.trim();
    if (!trimmed || report.recipients.includes(trimmed)) return;
    onChange({ recipients: [...report.recipients, trimmed] });
    setRecipientInput('');
  }

  function removeRecipient(email: string) {
    onChange({ recipients: report.recipients.filter((r) => r !== email) });
  }

  function toggleContent(c: string) {
    const has = report.contents.includes(c);
    onChange({
      contents: has ? report.contents.filter((x) => x !== c) : [...report.contents, c],
    });
  }

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <aside className="drawer" role="dialog" aria-label="Auto-Report bearbeiten">
        <header className="drawer-head">
          <h2>Auto-Report</h2>
          <span className={`badge ${report.active ? 'published' : 'hidden'}`}>
            {report.active ? 'Aktiv' : 'Inaktiv'}
          </span>
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Schließen">
            <span className="material-symbols-rounded">close</span>
          </button>
        </header>
        <div className="drawer-body">
          <div className="d-section">
            <div className="d-label">Name</div>
            <input
              type="text"
              value={report.name}
              onChange={(e) => onChange({ name: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-1)',
                borderRadius: 12,
                font: '500 14px/20px var(--font-body)',
                color: 'var(--fg-1)',
                outline: 'none',
              }}
            />
          </div>

          <div className="d-section">
            <div className="d-label">Inhalt</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {CONTENT_OPTIONS.map((c) => {
                const on = report.contents.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    className={`chip${on ? ' on' : ''}`}
                    onClick={() => toggleContent(c)}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="d-section">
            <div className="d-label">Rhythmus</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {(Object.keys(CADENCE_LABEL) as Cadence[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`chip${report.cadence === c ? ' on' : ''}`}
                  onClick={() => onChange({ cadence: c })}
                >
                  {CADENCE_LABEL[c]}
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <input
                type="text"
                placeholder={report.cadence === 'monthly' ? 'Tag (z.B. „1.")' : 'Wochentag'}
                value={report.cadenceDay}
                onChange={(e) => onChange({ cadenceDay: e.target.value })}
                style={inputStyle}
                disabled={report.cadence === 'daily'}
              />
              <input
                type="time"
                value={report.cadenceTime}
                onChange={(e) => onChange({ cadenceTime: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>

          <div className="d-section">
            <div className="d-label">Empfänger ({report.recipients.length})</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {report.recipients.map((email) => (
                <span
                  key={email}
                  className="chip"
                  style={{ paddingRight: 4, gap: 4 }}
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => removeRecipient(email)}
                    aria-label="Entfernen"
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 9,
                      border: 0,
                      background: 'transparent',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--fg-3)',
                    }}
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: 14 }}>close</span>
                  </button>
                </span>
              ))}
            </div>
            <input
              type="email"
              placeholder="E-Mail eingeben + Enter"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addRecipient(recipientInput);
                }
              }}
              style={inputStyle}
            />
          </div>

          <div className="d-section">
            <div className="d-label">Format</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['PDF', 'HTML', 'CSV', 'XLSX'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`chip${report.format === f ? ' on' : ''}`}
                  onClick={() => onChange({ format: f })}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="d-section">
            <div className="d-label">Aktivität</div>
            <div style={{ background: '#FAFBFC', border: '1px solid var(--border-1)', borderRadius: 12, padding: 12, font: '500 12px/18px var(--font-body)', color: 'var(--fg-2)' }}>
              <div>Nächster Versand: <span className="mono" style={{ color: 'var(--fg-1)' }}>{report.next}</span></div>
              <div>Letzte 30 Tage: 4× erfolgreich versendet · keine Bounces</div>
            </div>
          </div>
        </div>
        <footer className="drawer-foot">
          <button
            type="button"
            className="btn primary"
            onClick={() => onChange({ active: !report.active })}
          >
            <span className="material-symbols-rounded">{report.active ? 'pause' : 'play_arrow'}</span>
            {report.active ? 'Pausieren' : 'Aktivieren'}
          </button>
          <button type="button" className="btn">
            <span className="material-symbols-rounded">send</span>
            Jetzt senden
          </button>
          <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>
            Schließen
          </button>
        </footer>
      </aside>
    </>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  border: '1px solid var(--border-1)',
  borderRadius: 12,
  font: '500 13px/18px var(--font-body)',
  color: 'var(--fg-1)',
  outline: 'none',
  width: '100%',
};
